import { confirmSignIn, signIn } from '@backoffice-frontend/authentication'
import { LOGIN_INTENDED_PATH } from '@backoffice-frontend/common'
import {
  useMediaQuery,
  useMoiaErrorNotification,
  useTheme,
} from '@backoffice-frontend/patterns'
import { LoadingIndicator } from '@moia-dev/pace-web'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { PublicAppViewContentLayout } from '../components/PublicAppViewContentLayout'
import type { SessionTransferParams } from '../utils/sessionTransferParams'

type SessionTransferSignInProps = {
  params: SessionTransferParams
  onFailure: VoidFunction
  title: string
  description?: string
}

/**
 * Signs the user in via Cognito's Custom Auth flow using the session transfer
 * token the Admin Panel put in the URL hash, instead of showing the regular
 * login form. A successful `confirmSignIn` fires the same `signedIn` Hub
 * event a normal login does, so `PublicAppView` redirects onward from there.
 *
 * @see docs/architecture/impersonation-and-scoped-access.md in
 * moia-dev/backoffice-user-management for the full session transfer flow.
 */
export const SessionTransferSignIn = ({
  params,
  onFailure,
  title,
  description,
}: SessionTransferSignInProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()

  useEffect(() => {
    const performSessionTransfer = async () => {
      try {
        // A TBS session should always land on the start page.
        // Clear any previously set reroutes
        localStorage.removeItem(LOGIN_INTENDED_PATH)
        await signIn({
          username: params.cognitoUsername,
          options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
        })
        const { isSignedIn } = await confirmSignIn({
          challengeResponse: params.sessionTransferToken,
        })
        if (!isSignedIn) {
          throw new Error('User is not signed in after the session transfer')
        }
      } catch (error) {
        console.error('Session transfer sign-in failed:', error)
        enqueueMoiaErrorNotification(t('loginView_sessionTransferError'))
        onFailure()
      }
    }

    void performSessionTransfer()
  }, [params.sessionTransferToken])

  return (
    <PublicAppViewContentLayout
      title={title}
      isMobile={isMobile}
      description={description}
    >
      <div
        css={theme => ({
          display: 'flex',
          justifyContent: 'center',
          marginTop: theme.space.Space6,
        })}
      >
        <LoadingIndicator />
      </div>
    </PublicAppViewContentLayout>
  )
}
